import React, { useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { CompanyLocationsDetailOutDTO, DayEnum, OpeningHoursDayDTO, OpeningModeEnum } from '@api/ringil-api.v2';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import SectionEmptyState from './SectionEmptyState.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilDataTable, { ColumnConfig, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import EditOpeningHours from '@app/pages/customer/LocalityList/sidebars/EditOpeningHours.tsx';
import SvgTag from '@icons/tag-inverted.svg?react';
import SvgPencil from '@icons/pencil-inverted.svg?react';

interface ComponentProps {
  location: CompanyLocationsDetailOutDTO;
  onReload: () => void;
}

const WEEK_DAYS = [DayEnum.MONDAY, DayEnum.TUESDAY, DayEnum.WEDNESDAY, DayEnum.THURSDAY, DayEnum.FRIDAY, DayEnum.SATURDAY, DayEnum.SUNDAY];

const titleCaseDay = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

const OpeningHoursSection: React.FC<ComponentProps> = ({ location, onReload }) => {
  const { tr } = useTranslationLgs();
  const [visibleEditOpeningHours, setVisibleEditOpeningHours] = useState(false);

  // A deleted location is read-only everywhere — Restore on the detail is the only action offered.
  const readOnly = location.isDeleted;

  const dayFor = (day: DayEnum) => location.openingHourDefinitions?.openingHoursDays?.find(t => t.day === day);
  const hasOpeningHours = (location.openingHourDefinitions?.openingHoursDays ?? []).some(t => t.openingMode !== OpeningModeEnum.CLOSED);

  const renderDay = (day?: OpeningHoursDayDTO) => {
    if (day == null || day.openingMode === OpeningModeEnum.CLOSED) {
      return <span className="text-content-tertiary">{tr('LocalityDetail.closed', 'Closed')}</span>;
    }
    if (day.openingMode === OpeningModeEnum.OPEN) {
      return <span>{tr('LocalityDetail.allDay', 'non-stop')}</span>;
    }
    return (
      <div className="flex flex-col gap-2">
        {(day.openingHours ?? []).map((t, index) => (
          <div key={index}>
            {t.since}–{t.till}
          </div>
        ))}
      </div>
    );
  };

  const columnConfig: ColumnConfig<{ id: string }>[] = WEEK_DAYS.map((day, i) => ({
    id: `day-${day}`,
    header: titleCaseDay(tr(`TimeIntervalComponent.days${day}`, titleCaseDay(day))),
    width: `${100 / WEEK_DAYS.length}%`,
    order: i + 1,
    render: () => renderDay(dayFor(day)),
  }));

  const loadTableData = (filter: unknown, pageable: unknown, onSuccess: (pagedData: PagedData<{ id: string }>) => void) => {
    onSuccess({ data: [{ id: 'week' }], total: 1, page: 1, size: 1 });
  };

  return (
    <>
      <div>
        <SectionPanel
          icon={<SvgTag className={'w-6 h-6'} />}
          iconColor={'var(--content-tertiary)'}
          heading={tr('LocalityDetail.openingHours', 'Opening hours')}
          headerRight={
            <>
              <COTimezone timezoneId={location.timezoneId} dataTestId={'location-openingHours-timezone'} />
              {hasOpeningHours && !readOnly && (
                <RingilButton mode={'table'} icon={<SvgPencil />} onClick={() => setVisibleEditOpeningHours(true)} dataTestId={'location-openingHours-edit'} />
              )}
            </>
          }>
          {hasOpeningHours ? (
            <RingilDataTable
              columnConfig={columnConfig as ColumnConfig<unknown>[]}
              loadDataFromApi={loadTableData}
              filterName={'location_detail_opening_hours'}
              hidePaginator
            />
          ) : (
            <SectionEmptyState
              message={tr('LocalityDetail.openingHoursNotSet', 'Opening hours are not set.')}
              buttonLabel={readOnly ? undefined : tr('LocalityDetail.setOpeningHours', 'Set opening hours')}
              onButtonClick={readOnly ? undefined : () => setVisibleEditOpeningHours(true)}
              dataTestId={'location-openingHours-empty'}
            />
          )}
        </SectionPanel>
      </div>

      <EditOpeningHours
        visible={visibleEditOpeningHours}
        onHide={() => setVisibleEditOpeningHours(false)}
        onCompleted={() => {
          setVisibleEditOpeningHours(false);
          onReload();
        }}
        locationId={location.id}
      />
    </>
  );
};

export default OpeningHoursSection;
